'use client';

import { createContext, useContext, ReactNode, useState, useCallback, useRef } from 'react';

interface ScoreSaveContextValue {
    // Register a save function for a cluster
    registerSaveFunction: (cluster: string, saveFn: () => Promise<void>) => void;
    // Unregister when component unmounts
    unregisterSaveFunction: (cluster: string) => void;
    // Register hasUnsavedScores state for a cluster
    registerHasUnsaved: (cluster: string, hasUnsaved: boolean) => void;
    // Unregister hasUnsavedScores state (cleanup)
    unregisterHasUnsaved: (cluster: string) => void;
    // Save all scores across all clusters
    saveAllScores: () => Promise<void>;
    // Check if any cluster has unsaved scores (reactive)
    hasUnsavedScores: boolean;
}

const ScoreSaveContext = createContext<ScoreSaveContextValue | null>(null);

export function ScoreSaveProvider({ children }: { children: ReactNode }) {
    const saveFunctionsRef = useRef<Map<string, () => Promise<void>>>(new Map());
    const [unsavedByCluster, setUnsavedByCluster] = useState<Map<string, boolean>>(new Map());

    const registerSaveFunction = useCallback((cluster: string, saveFn: () => Promise<void>) => {
        saveFunctionsRef.current.set(cluster, saveFn);
    }, []);

    const unregisterSaveFunction = useCallback((cluster: string) => {
        saveFunctionsRef.current.delete(cluster);
    }, []);

    const unregisterHasUnsaved = useCallback((cluster: string) => {
        setUnsavedByCluster(prev => {
            const next = new Map(prev);
            next.delete(cluster);
            return next;
        });
    }, []);

    const registerHasUnsaved = useCallback((cluster: string, hasUnsaved: boolean) => {
        setUnsavedByCluster(prev => {
            const next = new Map(prev);
            next.set(cluster, hasUnsaved);
            return next;
        });
    }, []);

    const saveAllScores = useCallback(async () => {
        const promises: Promise<void>[] = [];
        for (const saveFn of saveFunctionsRef.current.values()) {
            promises.push(saveFn());
        }
        await Promise.all(promises);
    }, []);

    // Compute if any cluster has unsaved scores
    const hasUnsavedScores = Array.from(unsavedByCluster.values()).some(hasUnsaved => hasUnsaved);

    return (
        <ScoreSaveContext.Provider value={{
            registerSaveFunction,
            unregisterSaveFunction,
            registerHasUnsaved,
            unregisterHasUnsaved,
            saveAllScores,
            hasUnsavedScores,
        }}>
            {children}
        </ScoreSaveContext.Provider>
    );
}

export function useScoreSave() {
    const context = useContext(ScoreSaveContext);
    if (!context) {
        throw new Error('useScoreSave must be used within a ScoreSaveProvider');
    }
    return context;
}
